import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { WHEEL_FACTOR } from '../chart/useLiveDomain';
import type { MonitorPalette } from '../chart/palette';
import { binLabel, pct, pctInt } from '../chart/format';
import {
    forwardIndexAt,
    fwdCellAt,
    histCellAt,
    nearestPoint,
    type Hover,
    type Model,
    type Point,
} from './model';
import { renderChart } from './render';
import type { Strings } from './strings';

const CHIP_PAD_X = 6;
const CHIP_PAD_Y = 3;
const CHIP_GAP = 9;
const CHIP_CHAR_W = 6.1;
const DRAG_THRESHOLD_PX = 3;
const CLICK_DELAY_MS = 250;

interface PiprChartProps {
    model: Model;
    points: readonly Point[];
    palette: MonitorPalette;
    strings: Strings;
    historyLabel: string;
    highlightMeeting: number | null;
    hover: Hover;
    onHover: (hover: Hover) => void;
    onZoom: (t: number, factor: number) => void;
    onPan: (deltaMs: number) => void;
    onReset: () => void;
    onActivate: (hover: Exclude<Hover, null>) => void;
    canActivate: (hover: Exclude<Hover, null>) => boolean;
}

interface DragState {
    x: number;
    startX: number;
    moved: boolean;
}

function chipLabel(
    model: Model,
    points: readonly Point[],
    hover: Exclude<Hover, null>
): string {
    const n = model.deltas.length;
    if (hover.kind === 'hist') {
        const d = points[hover.i];
        if (!d) return '';
        const base = pct(d.implied, 3);
        if (hover.j === null) return base;
        return `${base}  ·  ${binLabel(model.deltas[hover.j], hover.j, n)} ${pctInt(d.p[hover.j])}`;
    }
    const m = model.fwd.meetings[hover.i];
    if (!m) return '';
    const base = pct(m.rateBp, 2);
    if (hover.j === null) return base;
    return `${base}  ·  ${binLabel(model.deltas[hover.j], hover.j, n)} ${pctInt(m.p[hover.j])}`;
}

function hoverAnchor(
    model: Model,
    points: readonly Point[],
    hover: Exclude<Hover, null>
): { px: number; py: number } | null {
    if (hover.kind === 'hist') {
        const d = points[hover.i];
        if (!d) return null;
        return {
            px: model.x(d.date),
            py: hover.j === null ? model.yPipr(d) : model.yCellHist(d, hover.j),
        };
    }
    if (hover.i >= model.nF) return null;
    return {
        px: model.xF(hover.i),
        py:
            hover.j === null
                ? model.yFwdPoint(hover.i)
                : model.yFwdCell(hover.i, hover.j),
    };
}

export function PiprChart(props: PiprChartProps) {
    const {
        model,
        points,
        palette,
        strings,
        historyLabel,
        highlightMeeting,
        hover,
    } = props;
    const svgRef = useRef<SVGSVGElement>(null);
    const histRef = useRef<SVGRectElement>(null);
    const dragRef = useRef<DragState | null>(null);
    const clickTimer = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => () => clearTimeout(clickTimer.current), []);
    const { layout } = model;
    const { marginLeft, plotTop, axisY, split, histW, fwdW, innerW } = layout;

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        renderChart(svg, model, {
            strings,
            palette,
            historyLabel,
            highlightMeeting,
        });
    }, [model, strings, palette, historyLabel, highlightMeeting]);

    const onZoom = props.onZoom;
    useEffect(() => {
        const el = histRef.current;
        if (!el) return;
        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            const rect = el.getBoundingClientRect();
            const t = model.x.invert(marginLeft + event.clientX - rect.left);
            onZoom(
                t.getTime(),
                event.deltaY > 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR
            );
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [model, marginLeft, onZoom]);

    const localXY = (event: React.PointerEvent<SVGRectElement>) => {
        const rect = svgRef.current?.getBoundingClientRect();
        return {
            x: event.clientX - (rect?.left ?? 0),
            y: event.clientY - (rect?.top ?? 0),
        };
    };

    const histHover = (x: number, y: number): Hover => {
        const d = nearestPoint(model, x);
        if (!d) return null;
        return { kind: 'hist', i: d.i, j: histCellAt(model, d, y) };
    };

    const fwdHover = (x: number, y: number): Hover => {
        const i = forwardIndexAt(model, x);
        if (i === null) return null;
        return { kind: 'fwd', i, j: fwdCellAt(model, i, y) };
    };

    const msPerPx = model.spanMs / Math.max(1, histW);
    const anchor = hover ? hoverAnchor(model, points, hover) : null;
    const label = hover ? chipLabel(model, points, hover) : '';
    const chipW = label.length * CHIP_CHAR_W + CHIP_PAD_X * 2;
    const chipH = 11 + CHIP_PAD_Y * 2;
    const rightEdge = marginLeft + innerW;
    const chipLeft = anchor
        ? anchor.px + CHIP_GAP + chipW <= rightEdge
            ? anchor.px + CHIP_GAP
            : anchor.px - CHIP_GAP - chipW
        : 0;
    const chipTop = anchor
        ? Math.max(plotTop, Math.min(axisY - chipH, anchor.py - chipH / 2))
        : 0;
    const linkable = hover ? props.canActivate(hover) : false;

    return (
        <Box
            sx={{ width: '100%', height: layout.height, position: 'relative' }}
        >
            <svg
                ref={svgRef}
                width={layout.width}
                height={layout.height}
                role="img"
                aria-label={strings.aria}
                style={{ display: 'block', position: 'absolute', inset: 0 }}
            />
            <svg
                width={layout.width}
                height={layout.height}
                style={{ display: 'block', position: 'absolute', inset: 0 }}
            >
                {anchor && (
                    <g pointerEvents="none">
                        <line
                            x1={anchor.px}
                            x2={anchor.px}
                            y1={plotTop}
                            y2={axisY}
                            stroke={palette.ink}
                            strokeWidth={0.8}
                            opacity={0.55}
                        />
                        <circle
                            cx={anchor.px}
                            cy={anchor.py}
                            r={4}
                            fill={palette.ink}
                        />
                        <rect
                            x={chipLeft}
                            y={chipTop}
                            width={chipW}
                            height={chipH}
                            rx={3}
                            fill={palette.chipBg}
                            stroke={palette.chipBorder}
                            strokeWidth={1}
                        />
                        <text
                            x={chipLeft + CHIP_PAD_X}
                            y={chipTop + chipH / 2 + 0.5}
                            fontSize={11}
                            fill={palette.ink}
                            dominantBaseline="middle"
                        >
                            {label}
                        </text>
                    </g>
                )}
                <rect
                    ref={histRef}
                    x={marginLeft}
                    y={plotTop}
                    width={histW}
                    height={axisY - plotTop}
                    fill="transparent"
                    style={{
                        cursor:
                            linkable && hover?.kind === 'hist'
                                ? 'pointer'
                                : 'crosshair',
                        touchAction: 'none',
                    }}
                    onPointerDown={event => {
                        if (event.button !== 0) return;
                        dragRef.current = {
                            x: event.clientX,
                            startX: event.clientX,
                            moved: false,
                        };
                        event.currentTarget.setPointerCapture(event.pointerId);
                    }}
                    onPointerMove={event => {
                        const drag = dragRef.current;
                        if (drag) {
                            if (
                                Math.abs(event.clientX - drag.startX) >
                                DRAG_THRESHOLD_PX
                            ) {
                                drag.moved = true;
                            }
                            const dx = event.clientX - drag.x;
                            if (drag.moved && dx !== 0) {
                                drag.x = event.clientX;
                                props.onPan(-dx * msPerPx);
                            }
                            return;
                        }
                        const { x, y } = localXY(event);
                        props.onHover(histHover(x, y));
                    }}
                    onPointerUp={event => {
                        const drag = dragRef.current;
                        dragRef.current = null;
                        event.currentTarget.releasePointerCapture(
                            event.pointerId
                        );
                        if (drag && !drag.moved) {
                            const { x, y } = localXY(event);
                            const h = histHover(x, y);
                            if (h && props.canActivate(h)) {
                                clearTimeout(clickTimer.current);
                                clickTimer.current = setTimeout(
                                    () => props.onActivate(h),
                                    CLICK_DELAY_MS
                                );
                            }
                        }
                    }}
                    onPointerLeave={() => {
                        if (!dragRef.current) props.onHover(null);
                    }}
                    onDoubleClick={() => {
                        clearTimeout(clickTimer.current);
                        props.onReset();
                    }}
                />
                <rect
                    x={split}
                    y={plotTop}
                    width={fwdW}
                    height={axisY - plotTop}
                    fill="transparent"
                    style={{
                        cursor:
                            linkable && hover?.kind === 'fwd'
                                ? 'pointer'
                                : 'crosshair',
                    }}
                    onPointerMove={event => {
                        const { x, y } = localXY(event);
                        props.onHover(fwdHover(x, y));
                    }}
                    onPointerLeave={() => props.onHover(null)}
                    onClick={event => {
                        const rect = svgRef.current?.getBoundingClientRect();
                        const h = fwdHover(
                            event.clientX - (rect?.left ?? 0),
                            event.clientY - (rect?.top ?? 0)
                        );
                        if (h && props.canActivate(h)) props.onActivate(h);
                    }}
                />
            </svg>
        </Box>
    );
}
